import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";

// Critical: Load Home component eagerly for fast LCP
import Home from "./pages/Home";

// Lazy load non-critical pages to reduce initial bundle
const Worksheets = lazy(() => import("./pages/Worksheets"));
const WorkshopsPublic = lazy(() => import("./pages/WorkshopsPublic"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Login = lazy(() => import("./pages/Login"));
const ParentLogin = lazy(() => import("./pages/ParentLogin"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const AuthCallback = lazy(() => import("./auth/AuthCallback"));
const ParentAuthCallback = lazy(() => import("./auth/ParentAuthCallback"));
const ProtectedRoute = lazy(() => import("./auth/ProtectedRoute"));
const ParentProtectedRoute = lazy(() => import("./auth/ParentProtectedRoute"));

// Loading fallback component - lightweight
const PageLoader = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 50%, #F0F9FF 100%)'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTopColor: '#1e3a8a',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 16px'
      }} />
      <p style={{ color: '#64748b', fontSize: '14px' }}>Loading...</p>
    </div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function AppRouter() {
  const location = useLocation();
  
  // Remove initial skeleton once React mounts
  useEffect(() => {
    const skeleton = document.getElementById('initial-skeleton');
    if (skeleton) {
      skeleton.remove();
    }
  }, []);
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    if (location.pathname.includes('/parent')) {
      return (
        <Suspense fallback={<PageLoader />}>
          <ParentAuthCallback />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }
  
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/worksheets" element={
        <Suspense fallback={<PageLoader />}>
          <Worksheets />
        </Suspense>
      } />
      <Route path="/workshops" element={
        <Suspense fallback={<PageLoader />}>
          <WorkshopsPublic />
        </Suspense>
      } />
      <Route path="/login" element={
        <Suspense fallback={<PageLoader />}>
          <Login />
        </Suspense>
      } />
      <Route 
        path="/dashboard" 
        element={
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          </Suspense>
        } 
      />
      {/* Parent Portal Routes */}
      <Route path="/parent/login" element={
        <Suspense fallback={<PageLoader />}>
          <ParentLogin />
        </Suspense>
      } />
      <Route 
        path="/parent/dashboard" 
        element={
          <Suspense fallback={<PageLoader />}>
            <ParentProtectedRoute>
              <ParentDashboard />
            </ParentProtectedRoute>
          </Suspense>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
